import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, CircularProgress, Container, Dialog, DialogContent, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { API_URL, IMAGE_URL } from "../../redux/Constant";
import { PURPLE, SectionLabel, parseAddress, cellSx } from "./OrderParts";

//live
const PAYPAL_CLIENT_ID =
  "AfQDuhlth4Tu0iQk-GSsyDkBkyMiTvUO94gLTE87LML_Xmon6mdrj5eWVZ8Rov9adcdapuLwxsqEJXzx";

  //sandbox
// const PAYPAL_CLIENT_ID =
//   "Acelpzhy_JEC4n44Uc8_r6a_UuZoy7i9yMfl_7Q5MZa5Kbxstyb6cixzJ0ny6cwWl7RMze2K_L3mUmYj";
const PAYPAL_NAMESPACE = "paypalRepay";

function loadPaypalSdk() {
  if (window[PAYPAL_NAMESPACE]) return Promise.resolve(window[PAYPAL_NAMESPACE]);
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&currency=USD&components=buttons`;
    script.setAttribute("data-namespace", PAYPAL_NAMESPACE);
    script.onload = () => resolve(window[PAYPAL_NAMESPACE]);
    script.onerror = () => reject(new Error("Failed to load PayPal"));
    document.body.appendChild(script);
  });
}

const isSandboxPayment = (order) => (order.transaction_body || "").includes("sandbox.paypal.com");
const isPaid = (order) => (order.payment_status || "").toUpperCase() === "COMPLETE";

const addressLabelSx = { fontSize: "11px", fontWeight: 700, textTransform: "uppercase", color: "#999", mb: 1, fontFamily: "ABLE" };
const lineSx = { fontFamily: "ABLE", fontSize: "14px" };

function AddressBlock({ addr }) {
  const f = (v) => (v ? String(v).trim() : "");
  const name = [f(addr.fname), f(addr.lname)].filter(Boolean).join(" ");
  const street = [addr.address_1, addr.address_2, addr.address_3, addr.address_4].filter(f).join(", ");
  const region = [addr.city, addr.state, addr.postal].filter(f).join(", ");

  return (
    <Box sx={{ color: "#444", lineHeight: 1.7 }}>
      {name && <Typography sx={{ ...lineSx, fontWeight: 600, color: "#000" }}>{name}</Typography>}
      {f(addr.company) && <Typography sx={lineSx}>{addr.company}</Typography>}
      {street && <Typography sx={lineSx}>{street}</Typography>}
      {region && <Typography sx={lineSx}>{region}</Typography>}
      {f(addr.country) && <Typography sx={lineSx}>{addr.country}</Typography>}
      {f(addr.phone) && (
        <Typography sx={{ ...lineSx, mt: 0.5 }}>
          <span style={{ color: "#999" }}>Phone:</span> {addr.phone}
        </Typography>
      )}
      {f(addr.email) && (
        <Typography sx={{ ...lineSx, wordBreak: "break-all" }}>
          <span style={{ color: "#999" }}>Email:</span> {addr.email}
        </Typography>
      )}
    </Box>
  );
}

const toPaypalAddress = (addr) => ({
  address_line_1: addr.address_1,
  address_line_2: addr.address_2,
  admin_area_2: addr.city,
  admin_area_1: addr.state,
  postal_code: addr.postal,
  country_code: addr.country,
});

// Prefill the PayPal checkout with the customer's details from the order.
function buildPaypalOrder(order) {
  const billing = parseAddress(order.billing_address_info);
  const shipping = parseAddress(order.shipping_address_info);
  const shipTo = shipping.address_1 ? shipping : billing;

  const payer = {};
  const email = billing.email || shipping.email || order.email;
  if (email) payer.email_address = email;
  if (billing.fname || billing.lname) {
    payer.name = { given_name: billing.fname || "", surname: billing.lname || "" };
  }
  if (billing.address_1 && billing.country) payer.address = toPaypalAddress(billing);

  const unit = {
    description: `Better Physiology Order #BP${order.order_id}`,
    custom_id: String(order.order_id),
    amount: {
      currency_code: "USD",
      value: Number(order.total_amount).toFixed(2),
    },
  };
  if (shipTo.address_1 && shipTo.country) {
    unit.shipping = {
      name: { full_name: [shipTo.fname, shipTo.lname].filter(Boolean).join(" ") },
      address: toPaypalAddress(shipTo),
    };
  }

  return { payer, purchase_units: [unit] };
}

const PayOrder = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paypalContainer, setPaypalContainer] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [paidTransactionId, setPaidTransactionId] = useState(null);
  const [successOpen, setSuccessOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    fetch(`${API_URL}/get/order/by/${id}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.status && data.items?.length) setOrder(data.items[0]);
        else setError(data.message || "Order not found");
      })
      .catch(() => setError("Failed to load order details"))
      .finally(() => setLoading(false));
  }, [id]);

  const needsPayment =
    order &&
    !paidTransactionId &&
    (order.order_status || "").toUpperCase() !== "CANCELLED" &&
    (!isPaid(order) || isSandboxPayment(order));

 
  const savePayment = async (details) => {
    const sandboxPaid = isSandboxPayment(order);
    const response = await fetch(
      `${API_URL}/shopping-cart/${sandboxPaid ? "repay-order" : "confirm-order"}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          order_id: order.order_id,
          transaction_body: JSON.stringify(details),
          transaction_id: details.id,
          ...(sandboxPaid
            ? {}
            : {
                shipping_method: order.shipping_method,
                discountCode: order.coupon_code,
                discount: order.discounted_amount,
              }),
        }),
      }
    );
    return response.json();
  };

  useEffect(() => {
    if (!paypalContainer || !needsPayment) return;
    let buttons;

    loadPaypalSdk()
      .then((paypal) => {
        buttons = paypal.Buttons({
          createOrder: (data, actions) => actions.order.create(buildPaypalOrder(order)),
          onApprove: async (data, actions) => {
            setProcessing(true);
            setPaymentError(null);
            try {
              const details = await actions.order.capture();
              const result = await savePayment(details);
              if (result.status) {
                setPaidTransactionId(details.id);
                setSuccessOpen(true);
              } else {
                setPaymentError(
                  `Payment received (transaction ${details.id}) but we could not update your order: ${result.message}. Please contact us with this transaction ID.`
                );
              }
            } catch (err) {
              console.error("Payment error:", err);
              setPaymentError("Something went wrong while processing the payment. Please try again or contact us.");
            } finally {
              setProcessing(false);
            }
          },
          onError: (err) => {
            console.error("PayPal Error:", err);
            setPaymentError("PayPal could not process the payment. Please try again.");
          },
        });
        return buttons.render(paypalContainer);
      })
      .catch((err) => {
        console.error(err);
        setPaymentError("Failed to load PayPal. Please refresh the page.");
      });

    return () => buttons?.close?.();
    // eslint-disable-next-line
  }, [paypalContainer, needsPayment]);

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress sx={{ color: PURPLE }} />
      </Box>
    );
  }

  if (error || !order) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
        <Box>
          <Typography sx={{ color: "#c62828", fontFamily: "ABLE", mb: 2 }}>
            {error || "Order not found"}
          </Typography>
          <Button
            type="button"
            onClick={() => navigate("/")}
            sx={{
              background: "rgb(109 79 160) !important",
              color: "#fff !important",
              fontFamily: "ABLE !important",
              fontSize: "0.92rem !important",
              lineHeight: "initial !important",
              padding: "0.6rem 1rem !important",
            }}
          >
            Go to Home
          </Button>
        </Box>
      </Box>
    );
  }

  const billing = parseAddress(order.billing_address_info);
  const shipping = parseAddress(order.shipping_address_info);
  const orderDate = order.created
    ? new Date(order.created).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
    : "—";

  return (
    <Box sx={{ minHeight: "100vh", background: "#f5f3fa", py: 4, px: 2 }}>
      <Container maxWidth={false} disableGutters sx={{ maxWidth: 1080 }}>
        <Box sx={{ background: "#fff", borderRadius: "12px", boxShadow: "rgb(146, 141, 191) 0px 2px 12px", overflow: "hidden",width:{lg:"85%",xs:"100%"},mx:"auto" }}>
          {/* Header */}
          <Box sx={{ background: PURPLE, px: 4, py: 2.5 }}>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "20px", fontFamily: "ABLE" }}>
              Order Payment
            </Typography>
            <Typography sx={{ color: "#f3eeff", fontSize: "14px", mt: 0.25, fontFamily: "ABLE" }}>
              Order #BP{order.order_id}
            </Typography>
          </Box>

          <Box sx={{ px: 4, py: 3, display: "flex", flexDirection: "column", gap: 3 }}>
            {needsPayment ? (
              <Typography sx={{ color: "#555", fontSize: "16px", fontFamily: "ABLE", lineHeight: 1.7,fontWeight: 600 }}>
                We were unable to collect payment for this order. Please review the details below
                and complete the payment using PayPal. We apologise for the inconvenience.
              </Typography>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CheckCircleIcon sx={{ color: "#2e7d32", fontSize: "24px" }} />
                  <Typography sx={{ color: "#2e7d32", fontWeight: 700, fontSize: "16px", fontFamily: "ABLE" }}>
                    {paidTransactionId
                      ? "Thank you! Your payment was successful."
                      : "Payment for this order has already been received. No action is needed."}
                  </Typography>
                </Box>
                {paidTransactionId && (
                  <Button
                    onClick={() => navigate(`/view-order/${order.order_id}`)}
                    sx={{
                      background: `${PURPLE} !important`,
                      color: "#fff !important",
                      fontFamily: "ABLE",
                      px: 3,
                      textTransform: "none",
                    }}
                  >
                    View Order
                  </Button>
                )}
              </Box>
            )}

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 2fr) minmax(0, 3fr)" }, gap: 3, alignItems: "start" }}>
              {/* Left: order details + addresses */}
              <Box>
                <SectionLabel>Order Details</SectionLabel>
                <Box sx={{ border: "1px solid #eee", borderRadius: "8px", overflow: "hidden" }}>
                  <Box sx={{ background: "#faf9fc", px: 2, py: 1.5, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                    <Typography sx={{ ...lineSx, color: "#666" }}>
                      Order <strong style={{ color: "#000" }}>#BP{order.order_id}</strong>
                    </Typography>
                    <Typography sx={{ ...lineSx, color: "#666" }}>{orderDate}</Typography>
                  </Box>
                  <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid #f0f0f0" }}>
                    <Typography sx={addressLabelSx}>Bill to</Typography>
                    <AddressBlock addr={billing} />
                  </Box>
                  <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid #f0f0f0" }}>
                    <Typography sx={addressLabelSx}>Ship to</Typography>
                    <AddressBlock addr={shipping} />
                  </Box>
                </Box>
              </Box>

              {/* Right: items + payment */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box>
                  <SectionLabel>Items Ordered</SectionLabel>
                  <Box sx={{ border: "1px solid #eee", borderRadius: "8px", overflow: "hidden" }}>
                    <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                      <Box component="thead" sx={{ background: "#faf9fc" }}>
                        <Box component="tr">
                          <Box component="th" sx={{ ...cellSx, textAlign: "left", color: "#666", fontWeight: 700 }}>Product</Box>
                          <Box component="th" sx={{ ...cellSx, textAlign: "center", color: "#666", fontWeight: 700 }}>Qty</Box>
                          <Box component="th" sx={{ ...cellSx, textAlign: "right", color: "#666", fontWeight: 700 }}>Unit Price</Box>
                          <Box component="th" sx={{ ...cellSx, textAlign: "right", color: "#666", fontWeight: 700 }}>Total</Box>
                        </Box>
                      </Box>
                      <Box component="tbody">
                        {order.items.map((item, i) => (
                          <Box component="tr" key={i} sx={{ borderTop: "1px solid #f0f0f0" }}>
                            <Box component="td" sx={cellSx}>
                              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                                {item.product_image && (
                                  <Box
                                    component="img"
                                    src={`${IMAGE_URL}/${item.product_image}`}
                                    alt={item.product_name}
                                    sx={{ width: 44, height: 44, objectFit: "contain", borderRadius: "4px", border: "1px solid #eee", flexShrink: 0, background: "#fff" }}
                                    onError={(e) => { e.target.style.display = "none"; }}
                                  />
                                )}
                                <Typography sx={{ fontFamily: "ABLE", fontSize: "14px", fontWeight: 600, color: "#000" }}>
                                  {item.product_name}
                                </Typography>
                              </Box>
                            </Box>
                            <Box component="td" sx={{ ...cellSx, textAlign: "center", color: "#555" }}>{item.quantity}</Box>
                            <Box component="td" sx={{ ...cellSx, textAlign: "right", color: "#555" }}>
                              ${Number(item.unit_price).toFixed(2)}
                            </Box>
                            <Box component="td" sx={{ ...cellSx, textAlign: "right", fontWeight: 700, color: "#000" }}>
                              ${(Number(item.unit_price) * Number(item.quantity)).toFixed(2)}
                            </Box>
                          </Box>
                        ))}

                        <Box component="tr" sx={{ background: "#faf9fc", borderTop: "1px solid #f0f0f0" }}>
                          <Box component="td" colSpan={3} sx={{ ...cellSx, color: "#666", fontWeight: 600 }}>Product Total</Box>
                          <Box component="td" sx={{ ...cellSx, textAlign: "right", fontWeight: 700, color: "#000" }}>
                            ${Number(order.product_total_price || 0).toFixed(2)}
                          </Box>
                        </Box>

                        {Number(order.Shipping_fee) > 0 && (
                          <Box component="tr" sx={{ borderTop: "1px solid #f0f0f0" }}>
                            <Box component="td" colSpan={3} sx={{ ...cellSx, color: "#666" }}>Shipping Fee</Box>
                            <Box component="td" sx={{ ...cellSx, textAlign: "right", fontWeight: 700, color: "#000" }}>
                              ${Number(order.Shipping_fee).toFixed(2)}
                            </Box>
                          </Box>
                        )}

                        {Number(order.discounted_amount) > 0 && (
                          <Box component="tr" sx={{ borderTop: "1px solid #f0f0f0" }}>
                            <Box component="td" colSpan={3} sx={{ ...cellSx, color: "#2e7d32" }}>
                              Discount{order.coupon_code ? ` (${order.coupon_code})` : ""}
                            </Box>
                            <Box component="td" sx={{ ...cellSx, textAlign: "right", fontWeight: 700, color: "#2e7d32" }}>
                              -${Number(order.discounted_amount).toFixed(2)}
                            </Box>
                          </Box>
                        )}

                        <Box component="tr" sx={{ background: PURPLE }}>
                          <Box component="td" colSpan={3} sx={{ ...cellSx, color: "#fff", fontWeight: 700 }}>Grand Total</Box>
                          <Box component="td" sx={{ ...cellSx, textAlign: "right", color: "#fff", fontWeight: 700 }}>
                            ${Number(order.total_amount || 0).toFixed(2)}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {needsPayment && (
                  <Box>
                  <SectionLabel>Payment</SectionLabel>
                  <Box sx={{ border: "1px solid #eee", borderRadius: "8px", p: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                      <Typography sx={{ ...lineSx, color: "#666", fontWeight: 600 }}>Amount due</Typography>
                      <Typography sx={{ fontFamily: "ABLE", fontSize: "18px", fontWeight: 700, color: "#000" }}>
                        ${Number(order.total_amount || 0).toFixed(2)}
                      </Typography>
                    </Box>

                    {paymentError && (
                      <Typography sx={{ color: "#c62828", fontFamily: "ABLE", fontSize: "14px", mb: 2 }}>
                        {paymentError}
                      </Typography>
                    )}

                    {processing && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                        <CircularProgress size={18} sx={{ color: PURPLE }} />
                        <Typography sx={{ fontFamily: "ABLE", fontSize: "14px" }}>Processing payment…</Typography>
                      </Box>
                    )}

                    <Box sx={{ display: processing ? "none" : "block" }}>
                      <div ref={setPaypalContainer}></div>
                    </Box>
                  </Box>
                  </Box>
                )}
              </Box>
            </Box>

            <Typography sx={{ fontFamily: "ABLE", fontSize: "13px", color: "#555", lineHeight: 1.7 }}>
              Questions about this order? Contact{" "}
              <a href="mailto:admin@betterphysiology.com" style={{ color: PURPLE }}>
                admin@betterphysiology.com
              </a>{" "}
              or call{" "}
              <a href="tel:+18008509795" style={{ color: PURPLE }}>
                +1.800.850.9795
              </a>.
            </Typography>
          </Box>
        </Box>
      </Container>

      {/* Payment success modal */}
      <Dialog
        open={successOpen}
        onClose={() => setSuccessOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogContent sx={{ textAlign: "center", px: 4, py: 4 }}>
          <CheckCircleIcon sx={{ color: "#2e7d32", fontSize: "64px", mb: 1 }} />
          <Typography sx={{ fontWeight: 700, fontSize: "20px", fontFamily: "ABLE", color: "#000", mb: 1 }}>
            Payment Successful
          </Typography>
          <Typography sx={{ fontFamily: "ABLE", fontSize: "14px", color: "#555", lineHeight: 1.7, mb: 2 }}>
            Thank you!<br/> We have received your payment of{" "}
            <strong>${Number(order.total_amount || 0).toFixed(2)}</strong> for order{" "}
            <strong>#BP{order.order_id}</strong>.
          </Typography>
          {paidTransactionId && (
            <Typography sx={{ fontFamily: "ABLE", fontSize: "13px", color: "#555", mb: 3, wordBreak: "break-all" }}>
              <span style={{ color: "#999" }}>Transaction ID: </span>
              <strong style={{ color: "#000" }}>{paidTransactionId}</strong>
            </Typography>
          )}
          <Box sx={{ display: "flex", justifyContent: "center", gap: 1.5 }}>
            <Button
              onClick={() => setSuccessOpen(false)}
              sx={{
                border: "2px solid #C5A7CF",
                color: PURPLE,
                fontFamily: "ABLE",
                px: 3,
                textTransform: "none",
                "&:hover": { background: "#f3eeff" },
              }}
            >
              Close
            </Button>
            <Button
              onClick={() => navigate(`/view-order/${order.order_id}`)}
              sx={{
                background: `${PURPLE} !important`,
                color: "#fff !important",
                fontFamily: "ABLE",
                px: 3,
                textTransform: "none",
              }}
            >
              View Order
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PayOrder;
